import React, { useState, useEffect } from 'react';
import { UserPlus, Mail, Shield, X, Edit2, Trash2, AlertCircle, CheckCircle, XCircle, Clock } from 'lucide-react';
import {
  fetchAllUsers,
  fetchPendingUsers,
  deleteUser,
  updateUser,
  approveUser,
  rejectUser,
  canApproveUsers,
  mapUserToUIFormat,
  mapUIFormatToUser
} from '../services/userServiceAppwrite';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [canApprove, setCanApprove] = useState(false);
  const [activeTab, setActiveTab] = useState('all'); // 'all' or 'pending'
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: 'Technician',
    status: 'Active',
    permissions: {
      viewDashboard: true,
      manageSamples: false,
      manageDewars: false,
      viewReports: true,
      manageUsers: false,
      editSettings: false
    }
  });

  // Load users and check approval permissions on mount
  useEffect(() => {
    loadUsers();
    checkApprovalPermissions();
  }, []);

  const checkApprovalPermissions = async () => {
    const hasPermission = await canApproveUsers();
    setCanApprove(hasPermission);
  };

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch all users
      const { data: allUsersData, error: fetchError } = await fetchAllUsers();
      if (fetchError) {
        setError(fetchError?.message || 'Failed to load users');
        setUsers([]);
        setPendingUsers([]);
        return;
      }

      // Fetch pending users
      const { data: pendingData, error: pendingError } = await fetchPendingUsers();
      if (pendingError) {
        console.error('Error loading pending users:', pendingError);
        setPendingUsers([]);
      } else {
        const formattedPending = pendingData?.map(mapUserToUIFormat) || [];
        setPendingUsers(formattedPending);
      }

      // Convert database format to UI format
      const formattedUsers = allUsersData?.map(mapUserToUIFormat) || [];
      setUsers(formattedUsers);
    } catch (err) {
      console.error('Error loading users:', err);
      setError(err?.message || 'An unexpected error occurred');
      setUsers([]);
      setPendingUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = () => {
    // Updated to redirect to signup page instead of showing modal
    window.location.href = '/signup';
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setFormData({
      name: user?.name,
      email: user?.email,
      role: user?.role,
      status: user?.status,
      permissions: { ...user?.permissions }
    });
    setShowModal(true);
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) {
      return;
    }

    try {
      setError(null);
      const { error: deleteError } = await deleteUser(userId);
      
      if (deleteError) {
        setError(deleteError?.message || 'Failed to delete user');
        return;
      }

      // Remove user from local state immediately for better UX
      setUsers(prevUsers => prevUsers?.filter(user => user?.id !== userId));
      
      // Optionally reload to ensure sync
      await loadUsers();
    } catch (err) {
      console.error('Error deleting user:', err);
      setError(err?.message || 'Failed to delete user');
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    try {
      setError(null);

      // Only allow editing existing users
      if (!editingUser) {
        setError('User creation must be done through the signup page');
        return;
      }

      // Check if trying to promote someone to admin
      const isPromotingToAdmin = formData?.role === 'Administrator' && editingUser?.role !== 'Administrator';
      
      if (isPromotingToAdmin) {
        // Show confirmation dialog about single admin rule
        const confirmed = window.confirm(
          'Only one administrator can exist at a time.\n\n' + 'Promoting this user to Administrator will automatically demote the current administrator to Lab Manager.\n\n'+ 'Do you want to proceed?'
        );
        
        if (!confirmed) {
          return;
        }
      }

      // Update existing user
      const updates = mapUIFormatToUser(formData);
      const { data, error: updateError } = await updateUser(editingUser?.id, updates);
      
      if (updateError) {
        setError(updateError?.message || 'Failed to update user');
        return;
      }

      // Update local state
      const updatedUser = mapUserToUIFormat(data);
      setUsers(prevUsers => prevUsers?.map(user => 
        user?.id === editingUser?.id ? updatedUser : user
      ));
      
      setShowModal(false);
      await loadUsers(); // Reload to ensure consistency and see demoted admin
    } catch (err) {
      console.error('Error saving user:', err);
      setError(err?.message || 'Failed to save user');
    }
  };

  const handleApproveUser = async (userId) => {
    try {
      setError(null);
      const { error: approveError } = await approveUser(userId);
      
      if (approveError) {
        setError(approveError?.message || 'Failed to approve user');
        return;
      }

      // Reload users to reflect changes
      await loadUsers();
    } catch (err) {
      console.error('Error approving user:', err);
      setError(err?.message || 'Failed to approve user');
    }
  };

  const handleRejectUser = async (userId) => {
    if (!window.confirm('Are you sure you want to reject this user?')) {
      return;
    }

    try {
      setError(null);
      const { error: rejectError } = await rejectUser(userId);
      
      if (rejectError) {
        setError(rejectError?.message || 'Failed to reject user');
        return;
      }

      // Reload users to reflect changes
      await loadUsers();
    } catch (err) {
      console.error('Error rejecting user:', err);
      setError(err?.message || 'Failed to reject user');
    }
  };

  const handlePermissionChange = (permission) => {
    setFormData({
      ...formData,
      permissions: {
        ...formData?.permissions,
        [permission]: !formData?.permissions?.[permission]
      }
    });
  };

  const handleRoleChange = (role) => {
    // Auto-set permissions based on role
    let permissions = { ...formData?.permissions };
    
    switch(role) {
      case 'Administrator':
        permissions = {
          viewDashboard: true,
          manageSamples: true,
          manageDewars: true,
          viewReports: true,
          manageUsers: true,
          editSettings: true
        };
        break;
      case 'Lab Manager':
        permissions = {
          viewDashboard: true,
          manageSamples: true,
          manageDewars: true,
          viewReports: true,
          manageUsers: false,
          editSettings: false
        };
        break;
      case 'Technician':
        permissions = {
          viewDashboard: true,
          manageSamples: true,
          manageDewars: false,
          viewReports: true,
          manageUsers: false,
          editSettings: false
        };
        break;
      case 'Student':
        permissions = {
          viewDashboard: true,
          manageSamples: false,
          manageDewars: false,
          viewReports: true,
          manageUsers: false,
          editSettings: false
        };
        break;
      default:
        break;
    }
    
    setFormData({
      ...formData,
      role,
      permissions
    });
  };

  const permissionLabels = {
    viewDashboard: 'View Dashboard',
    manageSamples: 'Manage Samples',
    manageDewars: 'Manage Dewars',
    viewReports: 'View Reports',
    manageUsers: 'Manage Users',
    editSettings: 'Edit Settings'
  };

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading users...</p>
        </div>
      </div>
    );
  }

  const displayUsers = activeTab === 'pending' ? pendingUsers : users;

  return (
    <div className="min-h-screen bg-gray-50 py-4 sm:py-6 lg:py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Error Display */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
            <AlertCircle className="w-5 h-5 text-red-600 mr-3 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm text-red-800">{error}</p>
              <button 
                onClick={() => setError(null)}
                className="text-sm text-red-600 hover:text-red-700 font-medium mt-2"
              >
                Dismiss
              </button>
            </div>
          </div>
        )}

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 sm:mb-8">
          <div className="mb-4 sm:mb-0">
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900">Users</h1>
            <p className="mt-2 text-sm sm:text-base text-gray-600">Manage user access and permissions</p>
          </div>
          <button 
            onClick={handleAddUser}
            className="flex items-center justify-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm sm:text-base"
            title="Redirects to signup page"
          >
            <UserPlus className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
            Add User
          </button>
        </div>

        {/* Tabs for All Users vs Pending Approval */}
        {canApprove && (
          <div className="mb-6 border-b border-gray-200">
            <nav className="flex space-x-8">
              <button
                onClick={() => setActiveTab('all')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'all' ?'border-indigo-600 text-indigo-600' :'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                All Users ({users?.length || 0})
              </button>
              <button
                onClick={() => setActiveTab('pending')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'pending' ?'border-indigo-600 text-indigo-600' :'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Pending Approval ({pendingUsers?.length || 0})
              </button>
            </nav>
          </div>
        )}

        {/* Empty State */}
        {displayUsers?.length === 0 && !loading && (
          <div className="text-center py-12">
            <p className="text-gray-600 text-lg">
              {activeTab === 'pending' ? 'No users pending approval' : 'No users found'}
            </p>
            {activeTab === 'all' && (
              <>
                <p className="text-gray-500 text-sm mt-2">New users can sign up through the signup page</p>
                <p className="text-gray-500 text-sm mt-1">Users will appear here automatically once they create an account</p>
              </>
            )}
          </div>
        )}

        {/* Users Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
          {displayUsers?.map((user) => (
            <div key={user?.id} className="bg-white rounded-lg shadow-md p-4 sm:p-6">
              <div className="flex items-start mb-4">
                <div className="w-12 h-12 sm:w-14 sm:h-14 bg-indigo-100 rounded-full flex items-center justify-center text-indigo-600 font-bold text-lg sm:text-xl mr-3 sm:mr-4 flex-shrink-0">
                  {user?.name?.charAt(0)}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-base sm:text-lg font-bold text-gray-900 truncate">{user?.name}</h3>
                  <div className="flex items-center text-xs sm:text-sm text-gray-600 mt-1">
                    <Mail className="w-3 h-3 mr-1 flex-shrink-0" />
                    <span className="truncate">{user?.email}</span>
                  </div>
                </div>
              </div>

              <div className="space-y-2 sm:space-y-3 pt-3 sm:pt-4 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center text-xs sm:text-sm text-gray-600">
                    <Shield className="w-4 h-4 mr-2 flex-shrink-0" />
                    Role
                  </div>
                  <span className="text-xs sm:text-sm font-medium text-gray-900">{user?.role}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs sm:text-sm text-gray-600">Status</span>
                  <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                    user?.status === 'Active' ? 'bg-green-100 text-green-800' : 
                    user?.status === 'Pending Approval' ? 'bg-yellow-100 text-yellow-800' :
                    user?.status === 'Pending Verification'? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {user?.status}
                  </span>
                </div>
                
                {/* Approval Actions - Only show for admins and pending users */}
                {canApprove && user?.status === 'Pending Approval' && (
                  <div className="flex gap-2 pt-2">
                    <button
                      onClick={() => handleApproveUser(user?.id)}
                      className="flex-1 flex items-center justify-center px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
                    >
                      <CheckCircle className="w-4 h-4 mr-1" />
                      Approve
                    </button>
                    <button
                      onClick={() => handleRejectUser(user?.id)}
                      className="flex items-center justify-center px-3 py-2 border border-red-300 text-red-700 rounded-lg hover:bg-red-50 transition-colors text-sm"
                    >
                      <XCircle className="w-4 h-4" />
                    </button>
                  </div>
                )}

                {/* Approval Info - Show who approved and when */}
                {user?.approved && user?.approved_by && (
                  <div className="pt-2 text-xs text-gray-500">
                    <Clock className="w-3 h-3 inline mr-1" />
                    Approved {user?.approved_at ? new Date(user?.approved_at)?.toLocaleDateString() : 'recently'}
                  </div>
                )}
                
                <div className="pt-2">
                  <p className="text-xs text-gray-600 mb-2">Permissions:</p>
                  <div className="flex flex-wrap gap-1">
                    {Object.entries(user?.permissions || {})?.filter(([_, value]) => value)?.map(([key]) => (
                        <span key={key} className="px-2 py-1 text-xs bg-indigo-50 text-indigo-700 rounded">
                          {permissionLabels?.[key]}
                        </span>
                      ))
                    }
                  </div>
                </div>
              </div>

              <div className="flex gap-2 mt-4">
                <button 
                  onClick={() => handleEditUser(user)}
                  className="flex-1 flex items-center justify-center px-3 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm"
                >
                  <Edit2 className="w-4 h-4 mr-1" />
                  Edit
                </button>
                <button 
                  onClick={() => handleDeleteUser(user?.id)}
                  className="flex items-center justify-center px-3 py-2 border border-red-300 text-red-700 rounded-lg hover:bg-red-50 transition-colors text-sm"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Edit User Modal (removed Add functionality) */}
      {showModal && editingUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">
                  Edit User
                </h2>
                <button 
                  onClick={() => setShowModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Name
                    </label>
                    <input
                      type="text"
                      required
                      value={formData?.name}
                      onChange={(e) => setFormData({ ...formData, name: e?.target?.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      placeholder="Enter user name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email
                    </label>
                    <input
                      type="email"
                      required
                      value={formData?.email}
                      onChange={(e) => setFormData({ ...formData, email: e?.target?.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      placeholder="Enter email address"
                      disabled={!!editingUser}
                    />
                    {editingUser && (
                      <p className="text-xs text-gray-500 mt-1">Email cannot be changed</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Role
                    </label>
                    <select
                      value={formData?.role}
                      onChange={(e) => handleRoleChange(e?.target?.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    >
                      <option value="Administrator">Administrator</option>
                      <option value="Lab Manager">Lab Manager</option>
                      <option value="Technician">Technician</option>
                      <option value="Student">Student</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Permissions (Based on Role)
                    </label>
                    <div className="space-y-2">
                      {Object.entries(permissionLabels)?.map(([key, label]) => (
                        <label key={key} className="flex items-center">
                          <input
                            type="checkbox"
                            checked={formData?.permissions?.[key]}
                            onChange={() => handlePermissionChange(key)}
                            className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                            disabled
                          />
                          <span className="ml-2 text-sm text-gray-700">{label}</span>
                        </label>
                      ))}
                    </div>
                    <p className="text-xs text-gray-500 mt-2">Permissions are determined by role</p>
                  </div>
                </div>

                <div className="flex gap-3 mt-6">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                  >
                    Update User
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;